'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CaseStudyLibraryProps {
  currentLanguage: string;
  selectedSector: string | null;
}

const CaseStudyLibrary = ({ currentLanguage, selectedSector }: CaseStudyLibraryProps) => {
  const [filters, setFilters] = useState({
    sector: selectedSector || 'all',
    region: 'all',
    scale: 'all'
  });

  const content = {
    en: {
      title: 'Case Study Library',
      subtitle: 'Learn from successful infrastructure sustainability projects',
      filterLabels: {
        sector: 'Sector',
        region: 'Region',
        scale: 'Project Scale'
      },
      filterOptions: {
        sector: ['All Sectors', 'Utilities', 'Ports', 'Ecosystem', 'Railways'],
        region: ['All Regions', 'India', 'Middle East', 'Europe', 'Asia Pacific'],
        scale: ['All Scales', 'Small (<$1M)', 'Medium ($1-10M)', 'Large (>$10M)']
      },
      caseStudies: [
        {
          id: 1,
          title: 'Smart Grid Implementation - Mumbai',
          sector: 'Utilities',
          region: 'India',
          scale: 'Large',
          impact: '45% reduction in peak load',
          duration: '18 months',
          investment: '$12M',
          summary: 'AI-powered grid optimization for 5 million households',
          metrics: ['2.5M tons CO₂ saved', '99.8% uptime', '35% cost reduction']
        },
        {
          id: 2,
          title: 'Green Port Initiative - Dubai',
          sector: 'Ports',
          region: 'Middle East',
          scale: 'Large',
          impact: '60% emission reduction',
          duration: '24 months',
          investment: '$25M',
          summary: 'LNG conversion and shore power for major port',
          metrics: ['150K tons NOx reduced', 'Zero SOx emissions', '40% fuel savings']
        },
        {
          id: 3,
          title: 'Desert Rejuvenation - Rajasthan',
          sector: 'Ecosystem',
          region: 'India',
          scale: 'Medium',
          impact: '30K hectares restored',
          duration: '36 months',
          investment: '$5M',
          summary: 'Native vegetation and water harvesting systems',
          metrics: ['300% biodiversity increase', '200K tons carbon', '500 jobs created']
        },
        {
          id: 4,
          title: 'Railway Electrification - Kerala',
          sector: 'Railways',
          region: 'India',
          scale: 'Large',
          impact: '80% emission reduction',
          duration: '30 months',
          investment: '$18M',
          summary: 'Complete electrification with renewable integration',
          metrics: ['1.2M tons CO₂ avoided', '50% energy savings', '100% renewable']
        }
      ]
    },
    hi: {
      title: 'केस स्टडी लाइब्रेरी',
      subtitle: 'सफल बुनियादी ढांचा स्थिरता परियोजनाओं से सीखें',
      filterLabels: {
        sector: 'क्षेत्र',
        region: 'क्षेत्र',
        scale: 'परियोजना स्केल'
      },
      filterOptions: {
        sector: ['सभी क्षेत्र', 'उपयोगिताएं', 'बंदरगाह', 'पारिस्थितिकी तंत्र', 'रेलवे'],
        region: ['सभी क्षेत्र', 'भारत', 'मध्य पूर्व', 'यूरोप', 'एशिया प्रशांत'],
        scale: ['सभी स्केल', 'छोटा (<$1M)', 'मध्यम ($1-10M)', 'बड़ा (>$10M)']
      },
      caseStudies: [
        {
          id: 1,
          title: 'स्मार्ट ग्रिड कार्यान्वयन - मुंबई',
          sector: 'उपयोगिताएं',
          region: 'भारत',
          scale: 'बड़ा',
          impact: 'पीक लोड में 45% की कमी',
          duration: '18 महीने',
          investment: '$12M',
          summary: '5 मिलियन घरों के लिए AI-संचालित ग्रिड अनुकूलन',
          metrics: ['2.5M टन CO₂ बचाया', '99.8% अपटाइम', '35% लागत में कमी']
        },
        {
          id: 2,
          title: 'ग्रीन पोर्ट पहल - दुबई',
          sector: 'बंदरगाह',
          region: 'मध्य पूर्व',
          scale: 'बड़ा',
          impact: '60% उत्सर्जन में कमी',
          duration: '24 महीने',
          investment: '$25M',
          summary: 'प्रमुख बंदरगाह के लिए LNG रूपांतरण और शोर पावर',
          metrics: ['150K टन NOx कम', 'शून्य SOx उत्सर्जन', '40% ईंधन बचत']
        },
        {
          id: 3,
          title: 'रेगिस्तान कायाकल्प - राजस्थान',
          sector: 'पारिस्थितिकी तंत्र',
          region: 'भारत',
          scale: 'मध्यम',
          impact: '30K हेक्टेयर बहाल',
          duration: '36 महीने',
          investment: '$5M',
          summary: 'देशी वनस्पति और जल संचयन प्रणाली',
          metrics: ['300% जैव विविधता वृद्धि', '200K टन कार्बन', '500 नौकरियां बनाई']
        },
        {
          id: 4,
          title: 'रेलवे विद्युतीकरण - केरल',
          sector: 'रेलवे',
          region: 'भारत',
          scale: 'बड़ा',
          impact: '80% उत्सर्जन में कमी',
          duration: '30 महीने',
          investment: '$18M',
          summary: 'नवीकरणीय एकीकरण के साथ पूर्ण विद्युतीकरण',
          metrics: ['1.2M टन CO₂ से बचा', '50% ऊर्जा बचत', '100% नवीकरणीय']
        }
      ]
    },
    ar: {
      title: 'مكتبة دراسات الحالة',
      subtitle: 'تعلم من مشاريع استدامة البنية التحتية الناجحة',
      filterLabels: {
        sector: 'القطاع',
        region: 'المنطقة',
        scale: 'حجم المشروع'
      },
      filterOptions: {
        sector: ['جميع القطاعات', 'المرافق', 'الموانئ', 'النظام البيئي', 'السكك الحديدية'],
        region: ['جميع المناطق', 'الهند', 'الشرق الأوسط', 'أوروبا', 'آسيا والمحيط الهادئ'],
        scale: ['جميع الأحجام', 'صغير (<$1M)', 'متوسط ($1-10M)', 'كبير (>$10M)']
      },
      caseStudies: [
        {
          id: 1,
          title: 'تنفيذ الشبكة الذكية - مومباي',
          sector: 'المرافق',
          region: 'الهند',
          scale: 'كبير',
          impact: 'تخفيض بنسبة 45٪ في الحمل الذروي',
          duration: '18 شهرًا',
          investment: '$12M',
          summary: 'تحسين الشبكة بالذكاء الاصطناعي لـ 5 ملايين أسرة',
          metrics: ['2.5 مليون طن CO₂ محفوظ', '99.8٪ وقت التشغيل', 'خفض التكلفة بنسبة 35٪']
        },
        {
          id: 2,
          title: 'مبادرة الميناء الأخضر - دبي',
          sector: 'الموانئ',
          region: 'الشرق الأوسط',
          scale: 'كبير',
          impact: 'خفض الانبعاثات بنسبة 60٪',
          duration: '24 شهرًا',
          investment: '$25M',
          summary: 'تحويل LNG وطاقة الشاطئ للميناء الرئيسي',
          metrics: ['150 ألف طن NOx مخفض', 'صفر انبعاثات SOx', 'توفير 40٪ من الوقود']
        },
        {
          id: 3,
          title: 'تجديد الصحراء - راجاستان',
          sector: 'النظام البيئي',
          region: 'الهند',
          scale: 'متوسط',
          impact: 'استعادة 30 ألف هكتار',
          duration: '36 شهرًا',
          investment: '$5M',
          summary: 'النباتات الأصلية وأنظمة حصاد المياه',
          metrics: ['زيادة التنوع البيولوجي بنسبة 300٪', '200 ألف طن كربون', '500 وظيفة تم إنشاؤها']
        },
        {
          id: 4,
          title: 'كهربة السكك الحديدية - كيرالا',
          sector: 'السكك الحديدية',
          region: 'الهند',
          scale: 'كبير',
          impact: 'خفض الانبعاثات بنسبة 80٪',
          duration: '30 شهرًا',
          investment: '$18M',
          summary: 'كهربة كاملة مع التكامل المتجدد',
          metrics: ['تجنب 1.2 مليون طن CO₂', 'توفير 50٪ من الطاقة', '100٪ متجدد']
        }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  const filteredCaseStudies = currentContent.caseStudies.filter(study => {
    const sectorMatch = filters.sector === 'all' || 
                       study.sector.toLowerCase() === filters.sector.toLowerCase() ||
                       (filters.sector === 'utilities' && study.sector === 'Utilities') ||
                       (filters.sector === 'ports' && study.sector === 'Ports') ||
                       (filters.sector === 'ecosystem' && study.sector === 'Ecosystem') ||
                       (filters.sector === 'railways' && study.sector === 'Railways');
    
    const regionMatch = filters.region === 'all' || study.region === filters.region;
    const scaleMatch = filters.scale === 'all' || study.scale === filters.scale;
    
    return sectorMatch && regionMatch && scaleMatch;
  });

  return (
    <section className="py-16 md:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Filters */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {currentContent.filterLabels.sector}
            </label>
            <select
              value={filters.sector}
              onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
              className="w-full px-4 py-2 bg-card border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {currentContent.filterOptions.sector.map((option, index) => (
                <option key={index} value={index === 0 ? 'all' : option.toLowerCase()}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {currentContent.filterLabels.region}
            </label>
            <select
              value={filters.region}
              onChange={(e) => setFilters({ ...filters, region: e.target.value })}
              className="w-full px-4 py-2 bg-card border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {currentContent.filterOptions.region.map((option, index) => (
                <option key={index} value={index === 0 ? 'all' : option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {currentContent.filterLabels.scale}
            </label>
            <select
              value={filters.scale}
              onChange={(e) => setFilters({ ...filters, scale: e.target.value })}
              className="w-full px-4 py-2 bg-card border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {currentContent.filterOptions.scale.map((option, index) => (
                <option key={index} value={index === 0 ? 'all' : option.match(/\(([^)]+)\)/)?.[1] || option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Case Studies */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredCaseStudies.map((study) => (
            <div 
              key={study.id}
              className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-natural transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                    {study.title}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full border border-primary/20">
                      {study.sector}
                    </span>
                    <span className="px-3 py-1 bg-secondary/10 text-secondary text-xs rounded-full border border-secondary/20">
                      {study.region}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-text-secondary mb-4">{study.summary}</p>

              <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-background rounded-lg">
                <div>
                  <div className="text-xs text-text-secondary mb-1">Impact</div>
                  <div className="font-semibold text-primary text-sm">{study.impact}</div>
                </div>
                <div>
                  <div className="text-xs text-text-secondary mb-1">Duration</div>
                  <div className="font-semibold text-text-primary text-sm">{study.duration}</div>
                </div>
                <div>
                  <div className="text-xs text-text-secondary mb-1">Investment</div>
                  <div className="font-semibold text-text-primary text-sm">{study.investment}</div>
                </div>
              </div>

              <div className="space-y-2">
                {study.metrics.map((metric, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-primary flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{metric}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredCaseStudies.length === 0 && (
          <div className="text-center py-12">
            <Icon name="FolderOpenIcon" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No case studies match your current filters.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseStudyLibrary;